import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaBuilding, FaWrench, FaBell, FaLock, FaPalette, FaUserTie, FaUserShield, FaHardHat, FaEye, FaBars, FaTimes } from 'react-icons/fa';
import { getApiEndpoint } from '../../../../config/api';
import './SettingsSidebar.css';

function SettingsSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const propertyDashMatch = location.pathname.match(/\/propertydashboard\/(\d+)\/settings/);
  const propertyMatch = location.pathname.match(/\/properties\/(\d+)\/settings/);
  const id = propertyDashMatch?.[1] || propertyMatch?.[1];

  let basePath;
  if (propertyDashMatch) {
    basePath = `/propertydashboard/${id}/settings`;
  } else if (propertyMatch) {
    basePath = `/properties/${id}/settings`;
  } else {
    basePath = '/settings';
  }

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    // Prefer JWT payload — profile endpoint can return a stale empty role for some users.
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload?.role) setUserRole(payload.role);
    } catch (_) { /* token malformed — fall through to API */ }
    fetch(getApiEndpoint('/api/users/profile'), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.role) setUserRole(data.role); })
      .catch(() => {});
  }, []);

  const isAdmin = userRole === 'super_admin' || userRole === 'admin' || userRole === 'company_admin';
  const isStaffOrAdmin = isAdmin || ['staff', 'cloud_staff', 'property-onboarding-staff', 'fm'].includes(userRole);

  const handleBack = () => {
    setMobileOpen(false);
    if (propertyDashMatch) {
      navigate(`/propertydashboard/${id}`);
    } else if (propertyMatch) {
      navigate(`/properties/${id}`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleNavClick = () => setMobileOpen(false);

  const navItems = (
    <>
      <button className="sidebar-back-btn" onClick={handleBack}>
        <FaArrowLeft /> {id ? 'Back to Property' : 'Back to Dashboard'}
      </button>

      <div className="settings-nav-group-label">General</div>
      <NavLink to={`${basePath}/company`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
        <span className="settings-nav-icon"><FaBuilding /></span><span>Company</span>
      </NavLink>
      <NavLink to={`${basePath}/application`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
        <span className="settings-nav-icon"><FaWrench /></span><span>Application</span>
      </NavLink>
      <NavLink to={`${basePath}/notifications`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
        <span className="settings-nav-icon"><FaBell /></span><span>Notifications</span>
      </NavLink>

      {isAdmin && (
        <>
          <div className="settings-nav-group-label">Security & Branding</div>
          <NavLink to={`${basePath}/security`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
            <span className="settings-nav-icon"><FaLock /></span><span>Security</span>
          </NavLink>
          <NavLink to={`${basePath}/branding`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
            <span className="settings-nav-icon"><FaPalette /></span><span>Branding</span>
          </NavLink>
        </>
      )}

      <div className="settings-nav-group-label">People</div>
      <NavLink to={`${basePath}/management`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
        <span className="settings-nav-icon"><FaUserTie /></span><span>Management Co.</span>
      </NavLink>
      {id && (
        <>
          <NavLink to={`${basePath}/building-managers`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
            <span className="settings-nav-icon"><FaUserShield /></span><span>Building Managers</span>
          </NavLink>
          <NavLink to={`${basePath}/contractors`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
            <span className="settings-nav-icon"><FaHardHat /></span><span>Contractors</span>
          </NavLink>
          <NavLink to={`${basePath}/quick-view`} className={({ isActive }) => `settings-nav-item ${isActive ? 'active' : ''}`} onClick={handleNavClick}>
            <span className="settings-nav-icon"><FaEye /></span><span>Quick View</span>
          </NavLink>
        </>
      )}
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="settings-sidebar">
        <div className="settings-nav">
          {navItems}
        </div>
      </aside>

      {/* Mobile bar */}
      <div className="settings-mobile-bar">
        <button className="settings-mobile-back" onClick={handleBack}>
          <FaArrowLeft /> Back
        </button>
        <span className="settings-mobile-title">Settings Menu</span>
        <button
          className="settings-mobile-toggle"
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label="Toggle settings menu"
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="settings-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile drawer */}
      <div className={`settings-mobile-drawer ${mobileOpen ? 'open' : ''}`}>
        <div className="settings-drawer-header">
          <span>Settings</span>
          <button className="settings-drawer-close" onClick={() => setMobileOpen(false)}>
            <FaTimes />
          </button>
        </div>
        <div className="settings-drawer-nav">
          {navItems}
        </div>
      </div>
    </>
  );
}

export default SettingsSidebar;
